
    import __i18nConfig from '@next-translate-root/i18n'
    import __appWithI18n from 'next-translate/appWithI18n'
    import { ComponentType, PropsWithChildren, useRef } from 'react';
import styled from '@emotion/styled';
import Sidebar from 'library/molecules/Sidebar';
import { useTheme } from '@emotion/react';
import Header from 'library/molecules/Header';
import LayoutProvider, { LayoutConfig, LayoutProvidersProps } from 'providers/LayoutProvider';
import useLayout from 'state/useLayout';
import SidebarProvider from 'providers/SidebarProvider';
import { useSidebar } from 'state/useSidebar';
import useOnClickOutside from 'shared/state/useOnClickOutside';
import ConditionalWrapper from 'shared/library/atoms/ConditionalWrapper';
import { coreTheme } from 'assets/theme/theme';
type PageLayoutProps = PropsWithChildren<Partial<LayoutConfig>>;
/**
 * This is a wrapper for the pages to facilitate subcomponents to interact with the page layout.
 *
 * Each page has its own LayoutProvider.
 */
const PageLayout = ({ children }: PageLayoutProps) => {
    const theme = useTheme();
    const { layout } = useLayout();
    const { sidebarIsVisible, toggleSidebarIsVisible } = useSidebar();
    const layoutConfig = theme.layout[layout];
    const menuRef = useRef(null);
    const sidebarRef = useRef(null);
    const refs = [menuRef, sidebarRef];
    const hasSidebarAndIsHidden = !sidebarIsVisible && layoutConfig.sidebar?.isIncluded;
    useOnClickOutside({
        refs,
        onClickOutside: () => {
            if (hasSidebarAndIsHidden)
                return;
            toggleSidebarIsVisible();
        },
        dependencyList: [...refs, hasSidebarAndIsHidden, toggleSidebarIsVisible]
    });
    return (<StyledPageLayout>
      {layoutConfig.header?.isIncluded && <Header menuRef={menuRef}/>}
      <StyledSideBarAndContentContainer>
        {layoutConfig.sidebar?.isIncluded && <Sidebar reference={sidebarRef}/>}
        {children}
      </StyledSideBarAndContentContainer>
    </StyledPageLayout>);
};
const withProviders = (Component: ComponentType<Pick<LayoutProvidersProps, 'children'>>) => {
    const Children = ({ layout, sidebar, children, ...other }: PropsWithChildren<Partial<LayoutConfig>>) => {
        /**
         * "useLayout" would be the best way to verify the layout information but we are sure "sidebar.isIncluded"
         * shouldn't change after the first render and we want to keep this section simple so it is ok to use
         * the following logic to confirm if we should include the SidebarProvider. We can introduce "useLayout"
         * if it ever need something more complex.
         *
         * If "layout" is undefined is because the page is using the default value which includes the sidebar.
         */
        const hasDefaultLayout = layout
            ? layout === coreTheme.layout.sidebarFull.id
            : !layout;
        const sidebarIsIncluded = hasDefaultLayout ?? !!sidebar?.isIncluded;
        return (<LayoutProvider initialLayout={{ layout, sidebar, ...other }}>
        <ConditionalWrapper condition={sidebarIsIncluded} wrapper={children => <SidebarProvider>{children}</SidebarProvider>}>
          <Component>{children}</Component>
        </ConditionalWrapper>
      </LayoutProvider>);
    };
    return Children;
};
const __Next_Translate__Page__1973fb7bf11__ = withProviders(PageLayout);
const StyledPageLayout = styled.div<Pick<PageLayoutProps, 'layout'>>(({ theme, layout }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    background: theme.colors.white,
    height: layout !== 'styleGuide' ? '100%' : undefined,
    overflow: layout !== 'styleGuide' ? 'auto' : undefined
}));
const StyledSideBarAndContentContainer = styled.div(() => ({
    display: 'flex',
    flex: 1,
    overflow: 'auto'
}));

    export default __appWithI18n(__Next_Translate__Page__1973fb7bf11__, {
      ...__i18nConfig,
      isLoader: true,
      skipInitialProps: false,
      loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
    });
  